import React from 'react';
import { useNavigate } from 'react-router-dom';

function PropResultsSection() {
  const navigate = useNavigate();

  return (
    <section className="py-20 px-6 bg-gradient-to-br from-gray-900 via-black to-gray-900 border-y border-zinc-800">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/50 px-4 py-2 rounded-full mb-4">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-400 font-bold text-sm uppercase tracking-wider">Verified FTMO Results</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white mb-4">
            Real Prop Firm Performance
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            See how Kasango AI Pro passed the FTMO Challenge with verified, transparent results
          </p>
        </div>

        {/* Results Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          
          {/* Card 1: Account */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-500 transition-all hover:scale-105">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="text-sm text-gray-400 mb-1">Account Size</div>
            <div className="text-2xl font-bold text-white">$100,000</div>
            <div className="text-xs text-gray-500 mt-1">FTMO Challenge</div>
          </div>

          {/* Card 2: Profit */}
          <div className="bg-gradient-to-br from-green-900 to-emerald-900 rounded-2xl p-6 border border-green-600 hover:border-green-400 transition-all hover:scale-105">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="text-sm text-green-300 mb-1">Total Profit</div>
            <div className="text-2xl font-bold text-white">+$5,127</div>
            <div className="text-xs text-green-400 mt-1">+5.1% Return</div>
          </div>

          {/* Card 3: Win Rate */}
          <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl p-6 border border-purple-600 hover:border-purple-400 transition-all hover:scale-105">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
            </div>
            <div className="text-sm text-purple-300 mb-1">Win Rate</div>
            <div className="text-2xl font-bold text-white">83.33%</div>
            <div className="text-xs text-purple-400 mt-1">10 of 12 Trades</div>
          </div>

          {/* Card 4: Status */}
          <div className="bg-gradient-to-br from-emerald-900 to-green-900 rounded-2xl p-6 border-2 border-emerald-500 hover:border-emerald-400 transition-all hover:scale-105">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="text-sm text-emerald-300 mb-1">Challenge Status</div>
            <div className="text-2xl font-bold text-white">PASSED ✓</div>
            <div className="text-xs text-emerald-400 mt-1">9 Trading Days</div>
          </div>

        </div>

        {/* Screenshots Preview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="relative group overflow-hidden rounded-2xl border border-gray-700 hover:border-green-500 transition-all">
            <img 
              src="https://customer-assets.emergentagent.com/job_tradewise-demo/artifacts/2mfevc0h_Screenshot%202026-01-16%20042330.png"
              alt="FTMO Trading Stats"
              className="w-full h-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-4 left-4">
                <p className="text-white font-semibold">FTMO Trading Statistics</p>
                <p className="text-gray-300 text-sm">Daily Performance & Metrics</p>
              </div>
            </div>
          </div>
          <div className="relative group overflow-hidden rounded-2xl border border-gray-700 hover:border-green-500 transition-all">
            <img 
              src="https://customer-assets.emergentagent.com/job_tradewise-demo/artifacts/0zv9m69x_Screenshot%202026-01-16%20042339.png"
              alt="FTMO Account MetriX"
              className="w-full h-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-4 left-4">
                <p className="text-white font-semibold">Account MetriX Dashboard</p>
                <p className="text-gray-300 text-sm">Equity Curve & Analysis</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 border border-green-500">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                See Full FTMO Challenge Results
              </h3>
              <p className="text-white/90">
                View complete trading statistics, daily breakdown, and verify on FTMO's official platform
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/prop-results')}
                className="px-8 py-4 bg-white hover:bg-gray-100 text-green-600 font-bold rounded-xl transition-all hover:scale-105 shadow-xl whitespace-nowrap"
              >
                View Full Report →
              </button>
              <a
                href="https://trader.ftmo.com/live-metrix/1511857119/share/019a115c-5860-73c3-a78b-22614390b044?lang=en"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-black/20 hover:bg-black/30 text-white font-bold rounded-xl transition-all hover:scale-105 whitespace-nowrap flex items-center gap-2"
              >
                Verify on FTMO
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Trust Badge */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            ✓ Official FTMO Challenge Results • Verified Performance • Transparent Trading
          </p>
        </div>

      </div>
    </section>
  );
}

export default PropResultsSection;
